import { useEffect } from "react";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useHistory } from "react-router-dom";
import axiosInstance from "../../../api";
import { useOktaAuth } from "@okta/okta-react";
import PatientDetailResponseModel from "../../../models/API/responseModels/patientDetailResponse.model";
import toastMessages from "../../helpers/toastMessage";

type UsePatientDetailOptions = {
  redirectOnError?: boolean;
};

function usePatientDetail(
  id: string,
  options?: UsePatientDetailOptions
): UseQueryResult<PatientDetailResponseModel> {
  const authState = useOktaAuth()?.authState;
  const history = useHistory();
  const redirectOnError = options?.redirectOnError ?? false;

  const query = useQuery({
    queryKey: ["patientDetail", { id }],
    queryFn: ({ queryKey }: any) =>
      axiosInstance
        .get(`/v1/patients/${queryKey[1]?.id}`, {
          headers: {
            Authorization: `Bearer ${authState?.accessToken?.accessToken}`,
          },
        })
        .then((res) => res.data),
    enabled: !!authState?.accessToken?.accessToken && !!id,
    refetchOnWindowFocus: false,
    retry: false,
  });

  useEffect(() => {
    if (!query.isError || !query.error) {
      return;
    }

    const error = query.error as AxiosError<{ message?: string }>;
    const errorMessage =
      error?.response?.data?.message ??
      error.message ??
      "Failed to load patient";

    toastMessages.error(errorMessage);

    if (redirectOnError) {
      history.push("/access/patients?page=1&size=10");
    }
  }, [query.isError, query.error, redirectOnError, history]);

  return query;
}

export default usePatientDetail;
